import React, { useState } from 'react'
import * as R from 'ramda'
import PropTypes from 'prop-types'
import { Typography } from '@material-ui/core'
import { useIntl, FormattedMessage } from 'react-intl'

import { useAppName, useAppLogo } from 'commons/usePartner'
import { RECEIVED, NOT_RECEIVED } from 'app/constants/messageDetail'

import DisplaySubscriber from './DisplaySubscriber'
import StackedBarChart from '../components/StackedBarChart'
import SubscriberList from './SubscriberList'
import LoaderStackedBar from 'app/main/messages/timeline/Skeleton/LoaderStackedBar'

import { SmsIcon, EmailIcon, AutomateCallIcon } from 'app/components/icons'

const DeliveryRate = (props) => {
  const { data, isLoading } = props
  const { app = null, sms = null, email = null, automated = null } = data || {}

  const { formatMessage } = useIntl()
  const { logo: appLogo } = useAppLogo()
  const [isOpen, setIsOpen] = useState(false)
  const [subscriber, setSubscriber] = useState({ data: [], title: '' })

  const appName = formatMessage(
    {
      id: 'notification.channel.app',
    },
    { appName: useAppName() },
  )
  const allReceivedCount = R.isEmpty(data)
    ? 0
    : R.sum([
        R.length(app?.[RECEIVED] ?? []),
        R.length(sms?.[RECEIVED] ?? []),
        R.length(email?.[RECEIVED] ?? []),
        R.length(automated?.[RECEIVED] ?? []),
      ])

  const allNotReceivedCount = R.isEmpty(data)
    ? 0
    : R.sum([
        R.length(app?.[NOT_RECEIVED] ?? []),
        R.length(sms?.[NOT_RECEIVED] ?? []),
        R.length(email?.[NOT_RECEIVED] ?? []),
        R.length(automated?.[NOT_RECEIVED] ?? []),
      ])

  const stackedInfo = (receivedCount, notReceivedCount) => {
    if (R.any(R.isNil(receivedCount), R.isNil(notReceivedCount))) return null
    else
      return [
        {
          name: RECEIVED,
          color: '#4FD975',
          lableTooltip: null,
          value: receivedCount,
        },
        {
          name: NOT_RECEIVED,
          color: '#EBECF4',
          lableTooltip: (
            <FormattedMessage
              id="notificationTimeline.card.metric.notificationDelivery.notDelivered.tooltips"
              values={{
                value: notReceivedCount,
              }}
            />
          ),
          value: notReceivedCount,
        },
      ]
  }

  const detailApp = formatMessage(
    {
      id: 'metrics.delivery.title.desescription.app',
    },
    { value: appName },
  )
  const detailSMS = formatMessage({
    id: 'metrics.delivery.title.desescription.sms',
  })
  const detailEmail = formatMessage({
    id: 'metrics.delivery.title.desescription.email',
  })
  const detailAutomated = formatMessage({
    id: 'metrics.delivery.title.desescription.automateCall',
  })

  const subscriberCountText = (channel) => {
    return `${R.length(channel[RECEIVED])}/${R.length([
      ...channel[RECEIVED],
      ...channel[NOT_RECEIVED],
    ])}`
  }

  const displayDescription = (image, name, detail, channel) => {
    return (
      <div className="my-3 space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            {image}
            <Typography className="font-bold flex items-center text-grey-400 font-medium">
              {name}
            </Typography>
          </div>
          <Typography className="font-bold flex items-center text-grey-400 font-medium">
            {subscriberCountText(channel)}
          </Typography>
        </div>
        <Typography className="text-12 text-grey-400">{detail}</Typography>
      </div>
    )
  }

  const handleSetSubscriber = (data, title) => {
    setIsOpen(true)
    setSubscriber({
      data: data,
      title: formatMessage({ id: title }),
    })
  }

  return (
    <div className="space-y-52 px-5 py-10">
      <div className="flex">
        <span
          className="w-3 h-3 mt-2 mr-3 rounded-full"
          style={{ backgroundColor: '#4FD975' }}
        />
        <Typography className="text-12 text-primary">
          <FormattedMessage
            id="metrics.delivery.rate.success.message"
            values={{
              b: (text) => <span className="font-bold">{text}</span>,
              to: R.sum([allReceivedCount, allNotReceivedCount]),
              from: allReceivedCount,
            }}
          />
        </Typography>
      </div>
      {isLoading ? (
        <LoaderStackedBar />
      ) : (
        <div className="space-y-10">
          {R.isNil(app) ? null : (
            <div className="space-y-3">
              {displayDescription(
                <img
                  src={appLogo}
                  className="object-contain mr-2"
                  width={20}
                  height={20}
                  alt="App"
                />,
                appName,
                detailApp,
                app,
              )}
              <StackedBarChart
                data={stackedInfo(
                  R.length(app[RECEIVED] ?? []),
                  R.length(app[NOT_RECEIVED] ?? []),
                )}
              />
              <div className="flex justify-between ">
                <div className="w-fit">
                  {R.isEmpty(app[RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={app[RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          app[RECEIVED],
                          'metrics.received.title',
                        )
                      }
                    />
                  )}
                </div>
                <div className="w-fit">
                  {R.isEmpty(app[NOT_RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={app[NOT_RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          app[NOT_RECEIVED],
                          'metrics.notReceived.title',
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {R.isNil(sms) ? null : (
            <div className="space-y-3">
              {displayDescription(
                <SmsIcon
                  color={'#00B2AD'}
                  classes="w-20"
                  notBackground={false}
                  backgroundColor={'#183560'}
                />,
                formatMessage({
                  id: 'channel.sms',
                }),
                detailSMS,
                sms,
              )}
              <StackedBarChart
                data={stackedInfo(
                  R.length(sms[RECEIVED]),
                  R.length(sms[NOT_RECEIVED]),
                )}
              />
              <div className="flex justify-between">
                <div className="w-fit">
                  {R.isEmpty(sms[RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={sms[RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          sms[RECEIVED],
                          'metrics.received.title',
                        )
                      }
                    />
                  )}
                </div>
                <div className="w-fit">
                  {R.isEmpty(sms[NOT_RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={sms[NOT_RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          sms[NOT_RECEIVED],
                          'metrics.notReceived.title',
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {R.isNil(email) ? null : (
            <div className="space-y-3">
              {displayDescription(
                <EmailIcon
                  color={'#00B2AD'}
                  classes="w-20"
                  notBackground={false}
                  backgroundColor={'#183560'}
                />,
                formatMessage({
                  id: 'channel.email',
                }),
                detailEmail,
                email,
              )}
              <StackedBarChart
                data={stackedInfo(
                  R.length(email[RECEIVED]),
                  R.length(email[NOT_RECEIVED]),
                )}
              />
              <div className="flex justify-between">
                <div className="w-fit">
                  {R.isEmpty(email[RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={email[RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          email[RECEIVED],
                          'metrics.received.title',
                        )
                      }
                    />
                  )}
                </div>
                <div className="w-fit">
                  {R.isEmpty(email[NOT_RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={email[NOT_RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          email[NOT_RECEIVED],
                          'metrics.notReceived.title',
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {R.isNil(automated) ? null : (
            <div className="space-y-3">
              {displayDescription(
                <AutomateCallIcon
                  color={'#00B2AD'}
                  classes="w-20"
                  notBackground={false}
                  backgroundColor={'#183560'}
                />,
                formatMessage({
                  id: 'channel.automatedCall',
                }),
                detailAutomated,
                automated,
              )}
              <StackedBarChart
                data={stackedInfo(
                  R.length(automated[RECEIVED]),
                  R.length(automated[NOT_RECEIVED]),
                )}
              />
              <div className="flex justify-between">
                <div className="w-fit">
                  {R.isEmpty(automated[RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={automated[RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          automated[RECEIVED],
                          'metrics.received.title',
                        )
                      }
                    />
                  )}
                </div>
                <div className="w-fit">
                  {R.isEmpty(automated[NOT_RECEIVED]) ? null : (
                    <DisplaySubscriber
                      data={automated[NOT_RECEIVED]}
                      onClick={() =>
                        handleSetSubscriber(
                          automated[NOT_RECEIVED],
                          'metrics.notReceived.title',
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <SubscriberList
        subscribers={subscriber.data}
        title={subscriber.title}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
      />
    </div>
  )
}

const object = {
  id: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  profilePicture: PropTypes.string,
  avatarCharacters: PropTypes.string,
  lastSeen: PropTypes.string,
}
const valuePropTypes = {
  received: PropTypes.arrayOf(PropTypes.shape(object)),
  notReceived: PropTypes.arrayOf(PropTypes.shape(object)),
}
DeliveryRate.propTypes = {
  data: PropTypes.shape({
    app: PropTypes.shape(valuePropTypes),
    automated: PropTypes.shape(valuePropTypes),
    email: PropTypes.shape(valuePropTypes),
    sms: PropTypes.shape(valuePropTypes),
  }).isRequired,
  isLoading: PropTypes.bool,
}

export default DeliveryRate
